import { BankLogo } from "@assets";
import { ErrorComponent } from "@components/ErrorComponent";
import { ListItem } from "@components/ListItem";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { AnalyticsEvent } from "@ctypes";
import { useBankLinkToken, useErrorBanner, useProcessBankLinkToken, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Analytics } from "@services";
import {
  PlaidLinkError,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from "react-plaid-link";
import { LinkedAccountsRouteProps } from "./types";

export const LinkedAccountsRoute = () => {
  const { token, loading, error } = useBankLinkToken();

  if (loading) return <Loader />;
  if (error || !token) return <ErrorComponent />;

  return <LinkedAccountsRouteComponent token={token} />;
};

export const LinkedAccountsRouteComponent = ({ token }: LinkedAccountsRouteProps) => {
  const { bankAccounts, refetch } = useUser();

  const [processPublicToken] = useProcessBankLinkToken();

  const { showErrorBanner } = useErrorBanner();

  const hasLinkedAccount = !!bankAccounts?.length;

  const props: MainLayoutProps = {
    pageHeader: i18n.t(
      hasLinkedAccount
        ? Translations.LinkedAccountsRouteTitle
        : Translations.LinkedAccountsRouteEmptyTitle,
    ),
  };

  const handleSuccess = async (
    publicToken: string | null,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    const institutionId = metadata.institution?.institution_id;

    if (!institutionId || !publicToken) return showErrorBanner();

    try {
      await processPublicToken({
        variables: {
          institutionId,
          publicToken,
        },
      });
    } catch {
    } finally {
      refetch();
      exit();
    }
  };

  const handleExit = (err: PlaidLinkError | null) => {
    if (err?.error_code) {
      Analytics.track(AnalyticsEvent.BankLinkingExit, {
        errorCode: err.error_code,
      });

      showErrorBanner();
    }
    exit();
  };

  const config: PlaidLinkOptions = {
    onSuccess: handleSuccess,
    onExit: handleExit,
    token,
  };

  const { exit } = usePlaidLink(config);

  return (
    <MainLayout {...props}>
      <Spacing size={SpacingSizes.S} />

      {bankAccounts?.map((account) => (
        <>
          <ListItem title={account.accountNumber ?? ""} leftIcon={<BankLogo />} rightIcon={null} />
          <Spacing size={SpacingSizes.S} />
        </>
      ))}
    </MainLayout>
  );
};
